import axios from "axios";
import ActivateClientRequest from "../models/serviceModels/ActivateClientRequest";
import Client from "../models/serviceModels/Client";
import CreateClientRequest from "../models/serviceModels/CreateClientRequest";
import DeactivateClientRequest from "../models/serviceModels/DeactivateClientRequest";
import Gender from "../models/serviceModels/Gender";
import PagedData from "../models/serviceModels/PagedData";
import Race from "../models/serviceModels/Race";
import Relationship from "../models/serviceModels/Relationship";
import SearchClientsRequest from "../models/serviceModels/SearchClientsRequest";
import UpdateClientRequest from "../models/serviceModels/UpdateClientRequest";
import { get, set } from "../utils/cacheData";
import { SearchClientNotesRequest } from "../models/serviceModels/SearchClientNotesRequest";
import { ClientNote } from "../models/serviceModels/ClientNote";
import SaveClientNoteRequest from "../models/serviceModels/SaveClientNoteRequest";
import UpdateClientNoteRequest from "../models/serviceModels/UpdateClientNoteRequest";
import DeleteClientNoteRequest from "../models/serviceModels/DeleteClientNoteRequest";
import Ethnicity from "../models/serviceModels/Ethnicity";
import ClientCasePlan from "../models/serviceModels/ClientCasePlan";
import CreateCasePlanRequest from "../models/serviceModels/CreateCasePlanRequest";
import DeletesCasePlanRequest from "../models/serviceModels/DeleteCasePlanRequest";
import GetCasePlanRequest from "../models/serviceModels/GetCasePlanRequest";
import UpdateCasePlanRequest from "../models/serviceModels/UpdateCasePlanRequest";
import CreateCasePlanGoalRequest from "../models/serviceModels/CreateCasePlanGoalRequest";
import CasePlanGoal from "../models/serviceModels/CasePlanGoal";
import UpdateCasePlanGoalRequest from "../models/serviceModels/UpdateCasePlanGoalRequest";
import DeleteCasePlanGoalRequest from "../models/serviceModels/DeleteCasePlanGoalRequest";
import CreateCasePlanGoalStatusRequest from "../models/serviceModels/CreateCasePlanGoalStatusRequest";
import CasePlanGoalStatus from "../models/serviceModels/CasePlanGoalStatus";
import { getItem, removeItem, setItem } from "./cacheService";
import CompleteGoalTask from "../models/serviceModels/CompleteGoalTask";
import CreateGoalTask from "../models/serviceModels/CreateGoalTask";
import GoalTask from "../models/serviceModels/GoalTask";
import UpdateGoalTask from "../models/serviceModels/UpdateGoalTask";
import DeleteGoalTask from "../models/serviceModels/DeleteGoalTask";
import CreatePlanNoteRequest from "../models/serviceModels/CreatePlanNoteRequest";
import { PlanNote } from "../models/serviceModels/PlanNote";
import UpdatePlanNoteRequest from "../models/serviceModels/UpdatePlanNoteRequest";
import DeletePlanNoteRequest from "../models/serviceModels/DeletePlanNoteRequest";
import { GoalNote } from "../models/serviceModels/GoalNote";
import GetGoalNotesRequest from "../models/serviceModels/GetGoalNotesRequest";
import CreateGoalNoteRequest from "../models/serviceModels/CreateGoalNoteRequest";
import UpdateGoalNoteRequest from "../models/serviceModels/UpdateGoalNoteRequest";
import DeleteGoalNoteRequest from "../models/serviceModels/DeleteGoalNoteRequest";
import StopCasePlanRequest from "../models/serviceModels/StopCasePlanRequest";
import StartCasePlanRequest from "../models/serviceModels/StartCasePlanRequest";
import GetPossibleDuplicateClientsRequest from "../models/serviceModels/GetPossibleDuplicateClientsRequest";
import PossibleDuplicateClient from "../models/serviceModels/PossibleDuplicateClient";
import GetClientNoteRequest from "../models/serviceModels/GetClientNoteRequest";
import GetClientByIdRequest from "../models/serviceModels/GetClientByIdRequest";
import MergeClientsRequest from "../models/serviceModels/MergeClientsRequest";
import MergeClientsResponse from "../models/serviceModels/MergeClientsResponse";
import DismissedClientDuplicate from "../models/serviceModels/DismissedClientDuplicate";
import DismissDuplicateRequest from "../models/serviceModels/DismissDuplicateRequest";
import { AssignClientsToUsers } from "../models/serviceModels/AssignClientsToUsers";
import PromoteHouseholdMemberRequest from "../models/serviceModels/PromoteHouseholdMemberRequest";
import PromoteHouseholdMemberResponse from "../models/serviceModels/PromoteHouseholdMemberResponse";
import PromoteHouseholdMembersRequest from "../models/serviceModels/PromoteHouseholdMembersRequest";
import PromoteHouseholdMembersResponse from "../models/serviceModels/PromoteHouseholdMembersResponse";
import MakeHouseholdMemberHeadOfHouseholdRequest from "../models/serviceModels/MakeHouseholdMemberHeadOfHouseholdRequest";
import MakeHouseholdMemberHeadOfHouseholdResponse from "../models/serviceModels/MakeHouseholdMemberHeadOfHouseholdResponse";
import CreateHouseholdMemberRequest from "../models/serviceModels/CreateHouseholdMemberRequest";
import UpdateHouseholdMemberRequest from "../models/serviceModels/UpdateHouseholdMemberRequest";
import HouseholdMember from "../models/serviceModels/HouseholdMember";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const DEMOGRAPHICS_CACHE_TTL_MS = 1000 * 60 * 5;

export interface ConfiguredRequiredField {
    id: string;
    organizationId: string;
    fieldName: string;
    effectiveDate?: string | null;
}

export interface BulkManagedProgram {
    id: string;
    name: string;
}

export interface BulkManagedParticipant {
    id: string;
    firstName: string;
    lastName: string;
    name: string;
    phone?: string | null;
    lastActivity?: string | null;
    daysInactive?: number | null;
    enrolledPrograms: BulkManagedProgram[];
}

export interface BulkParticipantFilters {
    programId?: string | null;
    inactiveDays?: string | number | null;
}

export interface BulkParticipantActions {
    deactivate?: {
        enabled: boolean;
        date?: string;
        unenrollPrograms?: boolean;
        unenrollProgramsDate?: string;
        unenrollProgramsReason?: string | null;
    };
    unenroll?: {
        programIds: string[];
        stopDate?: string;
        stopReason?: string | null;
    };
}

export interface BulkParticipantSelection {
    mode?: 'all' | 'ids';
    clientIds?: string[];
}

export interface BulkParticipantPreviewRequest {
    filters: BulkParticipantFilters;
    actions?: BulkParticipantActions;
    selection?: BulkParticipantSelection;
    skip?: number;
    take?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
}

export interface BulkProgramCount {
    programId: string;
    programName: string;
    count: number;
}

export interface BulkParticipantPreviewResponse {
    filters: BulkParticipantFilters;
    actions: BulkParticipantActions;
    participants: PagedData<BulkManagedParticipant>;
    selection: {
        totalSelected: number;
        deactivateCount: number;
        programCounts: BulkProgramCount[];
    };
}

export interface BulkActionBatch {
    id: string;
    createdBy: string;
    createdByFirstName?: string;
    createdByLastName?: string;
    created: string;
    started?: string | null;
    completed?: string | null;
    status: string;
    filters: BulkParticipantFilters;
    actions: BulkParticipantActions;
    totalCount: number;
    processedCount: number;
    successCount: number;
    failedCount: number;
    cancelledCount: number;
}

export interface BulkActionBatchItem {
    id: string;
    batchId: string;
    clientId: string;
    clientFirstName: string;
    clientLastName: string;
    clientName: string;
    status: string;
    actionDetails: BulkParticipantActions;
    result?: any;
    errorMessage?: string | null;
}

export interface DeleteHouseholdMemberRequest {
    clientId: string;
    householdMemberId: string;
}

const REQUIRED_FIELDS_CACHE_KEY = 'configured-required-fields';

const getRelationships = async (): Promise<Relationship[]> => {
    const cachedData = get<Relationship[]>('relationships');
    if (cachedData) return cachedData;

    const results = await axios.get(`${baseUrl}/relationships`);

    set<Race[]>('relationships', results.data, DEMOGRAPHICS_CACHE_TTL_MS);

    return results.data;
};

const deleteClient = async (clientId: string): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${clientId}`);
};

const getEthnicities = async (): Promise<Ethnicity[]> => {
  const cachedData = get<Ethnicity[]>('ethnicities');
  if (cachedData) return cachedData;
  
  const results = await axios.get(`${baseUrl}/ethnicities`);

  set<Ethnicity[]>('ethnicities', results.data, DEMOGRAPHICS_CACHE_TTL_MS);

  return results.data;
};

const searchClientNotes = async (request: SearchClientNotesRequest): Promise<PagedData<ClientNote>> => {
    const queryString = [
        `take=${request.take}`,
        `skip=${request.skip}`,
        `query=${request.query || ''}`
    ].join('&');
    
    const results = await axios.get(`${baseUrl}/clients/${request.clientId}/notes?${queryString}`);
    return results.data;
} 

const getClientNote = async (request: GetClientNoteRequest): Promise<ClientNote> => {
    const results = await axios.get(`${baseUrl}/clients/${request.clientId}/notes/${request.noteId}`);
    return results.data;
};

const getRaces = async (): Promise<Race[]> => {
    const cachedRaces = get<Race[]>('races');
    if (cachedRaces) return cachedRaces;

    const results = await axios.get(`${baseUrl}/races`);

    set<Race[]>('races', results.data, DEMOGRAPHICS_CACHE_TTL_MS);

    return results.data;
};

const getGenders = async (): Promise<Gender[]> => {
    const cachedData = get<Gender[]>('genders');
    if (cachedData) return cachedData;

    const results = await axios.get(`${baseUrl}/genders`);

    set<Gender[]>('genders', results.data, DEMOGRAPHICS_CACHE_TTL_MS);

    return results.data;
};

const mergeClients = async (request: MergeClientsRequest): Promise<MergeClientsResponse> => {
    const result = await axios.post(`${baseUrl}/clients/merge`, request);
    return result.data;
};

const getClientById = async (request: GetClientByIdRequest): Promise<Client> => {
    const queryParts = [];
    if (request.includeCasePlans) {
        queryParts.push('includeCasePlans=true');
    }

    if (request.includeFiles) {
        queryParts.push('includeFiles=true');
    }

    if (request.includeForms) {
        queryParts.push('includeForms=true');
    }

    if (request.includeNotes) {
        queryParts.push('includeNotes=true');
    }

    if (request.includePrograms) {
        queryParts.push('includePrograms=true');
    }

    if (request.includeAdditionalInformation) {
        queryParts.push('includeAdditionalInformation=true');
    }

    if (request.includeActivatedLog) {
        queryParts.push('includeActivatedLog=true')
    }

    const results = await axios.get(`${baseUrl}/clients/${request.id}?${queryParts.join('&')}`);
    return results.data;
};

const createClient = async (request: CreateClientRequest): Promise<Client> => {
    const result = await axios.post(`${baseUrl}/clients`, request);
    return result.data;
};

const updateClient = async (request: UpdateClientRequest): Promise<Client> => {
    const result = await axios.put(`${baseUrl}/clients/${request.id}`, request);
    return result.data;
};

const searchClients = async (request: SearchClientsRequest): Promise<PagedData<Client>> => {
    const result = await axios.get(`${baseUrl}/clients?query=${request.query}&skip=${request.skip}&take=${request.take}&programId=${request.programId || ''}&includeDeactivated=${request.includeDeactivated}`);
    return result.data;
}

const activateClient = async (request: ActivateClientRequest): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/activate`, {
        date: request.date
    });
};

const deactivateClient = async (request: DeactivateClientRequest): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/deactivate`, {
        date: request.date,
        unenrollPrograms: request.unenrollPrograms,
        unenrollProgramsDate: request.unenrollProgramsDate,
        unenrollProgramsReason: request.unenrollProgramsReason
    });
};

const saveClientNote = async (request: SaveClientNoteRequest): Promise<ClientNote> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/notes`, {
        value: request.value,
        files: request.files || []
    });

    return result.data;
};

const updateClientNote = async (request: UpdateClientNoteRequest): Promise<ClientNote> => {
    const result = await axios.put(`${baseUrl}/clients/${request.clientId}/notes/${request.id}`, {
        value: request.value,
        files: request.files || []
    });
    
    return result.data;
};

const deleteClientNote = async (request: DeleteClientNoteRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/notes/${request.id}`);
};

const getClientCasePlans = async (clientId: string, sortBy?: string, sortDirection?: 'asc' | 'desc'): Promise<ClientCasePlan[]> => {
    const result = await axios.get(`${baseUrl}/clients/${clientId}/case-plans`, { params: { sortBy, sortDirection } });
    return result.data;
};

const createCasePlan = async (request: CreateCasePlanRequest): Promise<ClientCasePlan> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans`, {
        name: request.name, 
        started: request.started
    });
    
    return result.data;
};

const getCasePlan = async (request: GetCasePlanRequest): Promise<ClientCasePlan> => {
    const result = await axios.get(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}`);
    return result.data;
};

const updateCasePlan = async ( request: UpdateCasePlanRequest): Promise<ClientCasePlan> => {
    const result = await axios.put(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}`, {
        name: request.name,
        started: request.started
    });

    return result.data;
};

const createCasePlanGoal = async (request: CreateCasePlanGoalRequest): Promise<CasePlanGoal> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals`, {
        name: request.name, 
        dueDate: request.dueDate,
        libraryGoalId: request.libraryGoalId
    });

    return result.data;
};

const updateCasePlanGoal = async (request: UpdateCasePlanGoalRequest): Promise<CasePlanGoal> => {
    const result = await axios.put(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}`, {
        name: request.name, 
        dueDate: request.dueDate,
        order: request.order,
        libraryGoalId: request.libraryGoalId
    });

    return result.data;
};

const getPossibleDuplicateClients = async (request: GetPossibleDuplicateClientsRequest): Promise<PossibleDuplicateClient[]> => {
    const queryParts: string[] = [];

    queryParts.push(`firstName=${request.firstName || ''}`);
    queryParts.push(`lastName=${request.lastName || ''}`);
    queryParts.push(`birthDate=${request.birthDate || ''}`);

    const result = await axios.get(`${baseUrl}/clients/duplicates?${queryParts.join("&")}`);

    return result.data;
}

const getDismissedDuplicateClients = async (): Promise<DismissedClientDuplicate[]> => {
    const result = await axios.get(`${baseUrl}/clients/duplicates/dismissed`);
    return result.data;
};

const getAllPossibleDuplicateClients = async (): Promise<{sourceClient: PossibleDuplicateClient, possibleDuplicates: PossibleDuplicateClient[] }[]> => {
    const result = await axios.get(`${baseUrl}/clients/duplicates/all`);
    return result.data;
};

const createCasePlanGoalStatus = async (request: CreateCasePlanGoalStatusRequest): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/status`, {
        statusId: request.statusId,
        statusDate: request.statusDate
    });
};

const deleteCasePlanGoal = async (request: DeleteCasePlanGoalRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}`);
};

const getCasePlanGoalStatuses = async (): Promise<CasePlanGoalStatus[]> => {
    const cachedStatuses = getItem<CasePlanGoalStatus[]>('goal-statuses');
    if (cachedStatuses) {
        return cachedStatuses;
    }

    const result = await axios.get(`${baseUrl}/clients/case-plans/goals/statuses`);
    setItem<CasePlanGoalStatus[]>('goal-statuses', result.data);
    return result.data;
};

const deleteCasePlan = async (request: DeletesCasePlanRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/case-plans/${request.id}`);
};

const completeGoalTask = async (request: CompleteGoalTask): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/tasks/${request.taskId}/complete`);
};

const uncompleteGoalTask = async (request: CompleteGoalTask): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/tasks/${request.taskId}/uncomplete`);
};

const createGoalTask = async (request: CreateGoalTask): Promise<GoalTask> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/tasks`, {
        text: request.text || ''
    });

    return result.data;
};

const updateGoalTask = async (request: UpdateGoalTask): Promise<GoalTask> => {
    const result = await axios.put(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/tasks/${request.taskId}`, {
        text: request.text || '',
        order: request.order
    });

    return result.data;
};

const deleteGoalTask = async (request: DeleteGoalTask): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/tasks/${request.taskId}`);
};

const stopCasePlan = async (request: StopCasePlanRequest): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/stop`, {
        stopped: request.stopped
    });
};

const startCasePlan = async (request: StartCasePlanRequest): Promise<void> => {
    await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/start`, {
        started: request.started
    });
};

const createPlanNote = async (request: CreatePlanNoteRequest): Promise<PlanNote> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/notes`,{
        text: request.text,
        files: request.files
    });

    return result.data;
};

const getGoalNotes = async (request: GetGoalNotesRequest): Promise<GoalNote[]> => {
    const result = await axios.get(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/notes`);
    return result.data;
};

const createGoalNote = async (request: CreateGoalNoteRequest): Promise<GoalNote> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/notes`, {
        text: request.text,
        files: request.files
    });

    return result.data;
};

const updateGoalNote = async (request: UpdateGoalNoteRequest): Promise<GoalNote> => {
    const result = await axios.put(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/notes/${request.noteId}`, {
        text: request.text,
        files: request.files
    });

    return result.data;
};

const deleteGoalNote = async (request: DeleteGoalNoteRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/goals/${request.goalId}/notes/${request.noteId}`);
};

const dismissDuplicate = async (request: DismissDuplicateRequest): Promise<DismissedClientDuplicate[]> => {
    const result = await axios.post(`${baseUrl}/clients/duplicates/dismissed`, {
        clientId1: request.clientId1,
        clientId2: request.clientId2
    });

    return result.data;
}

const revertDismissedDuplicate = async (dismissedId: string): Promise<DismissedClientDuplicate[]> => {
    const result = await axios.delete(`${baseUrl}/clients/duplicates/dismissed/${dismissedId}`);
    return result.data;
};

const updatePlanNote = async (request: UpdatePlanNoteRequest): Promise<PlanNote> => {
    const result = await axios.put(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/notes/${request.noteId}`,{
        text: request.text,
        files: request.files
    });

    return result.data;
};

const deletePlanNote = async (request: DeletePlanNoteRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/case-plans/${request.casePlanId}/notes/${request.noteId}`);
};

const assignClientsToUsers = async (clientIds: string[], assignToUserId: string): Promise<AssignClientsToUsers> => {
    const result = await axios.post(`${baseUrl}/clients/assign-clients`, {
        clientIds,
        assignToUserId
    });

    return result.data;
};

// Unassign clients from users
const unAssignClientsFromUsers = async (clients : { clientId: string, userId: string }[]): Promise<void> => {
    await axios.post(`${baseUrl}/clients/unassign-clients`, {
        clients
    });
}

const promoteHouseholdMember = async (request: PromoteHouseholdMemberRequest): Promise<PromoteHouseholdMemberResponse> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/household-members/${request.householdMemberId}/promote`);
    return result.data;
}

const promoteHouseholdMembers = async (request: PromoteHouseholdMembersRequest): Promise<PromoteHouseholdMembersResponse> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/household-members/promote`, {
        householdMemberIds: request.householdMemberIds
    });
    return result.data;
}

const makeHouseholdMemberHeadOfHousehold = async (request: MakeHouseholdMemberHeadOfHouseholdRequest): Promise<MakeHouseholdMemberHeadOfHouseholdResponse> => {
    const result = await axios.post(`${baseUrl}/clients/${request.clientId}/household-members/${request.householdMemberId}/make-head`);
    return result.data;
}

const createHouseholdMember = async (clientId: string, request: CreateHouseholdMemberRequest): Promise<HouseholdMember> => {
    const result = await axios.post(`${baseUrl}/clients/${clientId}/household-members`, request);
    return result.data;
}

const updateHouseholdMember = async (clientId: string, householdMemberId: string, request: UpdateHouseholdMemberRequest): Promise<HouseholdMember> => {
    const result = await axios.put(`${baseUrl}/clients/${clientId}/household-members/${householdMemberId}`, request);
    return result.data;
}

const deleteHouseholdMember = async (request: DeleteHouseholdMemberRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/clients/${request.clientId}/household-members/${request.householdMemberId}`);
}

const getConfiguredRequiredFields = async (): Promise<ConfiguredRequiredField[]> => {
    const cachedFields = getItem<ConfiguredRequiredField[]>(REQUIRED_FIELDS_CACHE_KEY);
    if (cachedFields) return cachedFields;

    const result = await axios.get(`${baseUrl}/clients/configured-required-fields`);
    setItem<ConfiguredRequiredField[]>(REQUIRED_FIELDS_CACHE_KEY, result.data, DEMOGRAPHICS_CACHE_TTL_MS);
    return result.data;
}

const clearConfiguredRequiredFieldsCache = () => {
    removeItem(REQUIRED_FIELDS_CACHE_KEY);
}

const previewBulkParticipantActions = async (request: BulkParticipantPreviewRequest): Promise<BulkParticipantPreviewResponse> => {
    const result = await axios.post(`${baseUrl}/clients/bulk-management/preview`, request);
    return result.data;
};

const createBulkParticipantActionBatch = async (request: BulkParticipantPreviewRequest): Promise<BulkActionBatch> => {
    const result = await axios.post(`${baseUrl}/clients/bulk-management/batches`, request);
    return result.data;
};

const getBulkParticipantActionBatches = async (skip = 0, take = 20, sortBy?: string, sortDirection?: 'asc' | 'desc'): Promise<BulkActionBatch[]> => {
    const result = await axios.get(`${baseUrl}/clients/bulk-management/batches`, { params: { skip, take, sortBy, sortDirection } });
    return result.data;
};

const getBulkParticipantActionBatch = async (batchId: string): Promise<BulkActionBatch> => {
    const result = await axios.get(`${baseUrl}/clients/bulk-management/batches/${batchId}`);
    return result.data;
};

const getBulkParticipantActionBatchItems = async (batchId: string, skip = 0, take = 100, sortBy?: string, sortDirection?: 'asc' | 'desc'): Promise<BulkActionBatchItem[]> => {
    const result = await axios.get(`${baseUrl}/clients/bulk-management/batches/${batchId}/items`, { params: { skip, take, sortBy, sortDirection } });
    return result.data;
};

const cancelBulkParticipantActionBatch = async (batchId: string): Promise<BulkActionBatch> => {
    const result = await axios.post(`${baseUrl}/clients/bulk-management/batches/${batchId}/cancel`);
    return result.data;
};

const forceCancelBulkParticipantActionBatch = async (batchId: string): Promise<BulkActionBatch> => {
    const result = await axios.post(`${baseUrl}/clients/bulk-management/batches/${batchId}/force-cancel`);
    return result.data;
};

export {
    getRelationships,
    getRaces,
    deleteClient,
    getGenders,
    getClientById,
    createClient,
    updateClient,
    searchClients,
    activateClient,
    deactivateClient,
    searchClientNotes,
    saveClientNote,
    updateClientNote,
    deleteClientNote,
    getEthnicities,
    getClientCasePlans,
    createCasePlan,
    deleteCasePlan,
    getCasePlan,
    updateCasePlan,
    createCasePlanGoal,
    updateCasePlanGoal,
    deleteCasePlanGoal,
    createCasePlanGoalStatus,
    getCasePlanGoalStatuses,
    completeGoalTask,
    uncompleteGoalTask,
    createGoalTask,
    updateGoalTask,
    deleteGoalTask,
    stopCasePlan,
    startCasePlan,
    createPlanNote,
    updatePlanNote,
    deletePlanNote,
    getGoalNotes,
    createGoalNote,
    updateGoalNote,
    deleteGoalNote,
    getPossibleDuplicateClients,
    getClientNote,
    mergeClients,
    getAllPossibleDuplicateClients,
    getDismissedDuplicateClients,
    revertDismissedDuplicate,
    dismissDuplicate,
    assignClientsToUsers,
    unAssignClientsFromUsers,
    promoteHouseholdMember,
    promoteHouseholdMembers,
    makeHouseholdMemberHeadOfHousehold,
    createHouseholdMember,
    updateHouseholdMember,
    deleteHouseholdMember,
    getConfiguredRequiredFields,
    clearConfiguredRequiredFieldsCache,
    previewBulkParticipantActions,
    createBulkParticipantActionBatch,
    getBulkParticipantActionBatches,
    getBulkParticipantActionBatch,
    getBulkParticipantActionBatchItems,
    cancelBulkParticipantActionBatch,
    forceCancelBulkParticipantActionBatch
};
